import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, XCircle, ExternalLink, AlertTriangle, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Check = { name: string; ok: boolean; detail?: string };
type Report = {
  ok: boolean;
  mode: "live" | "test" | "unknown";
  account?: { id: string; business_name?: string; charges_enabled?: boolean; payouts_enabled?: boolean } | null;
  webhook_secret_present: boolean;
  last_processed?: { type: string; processed_at: string; stripe_event_id: string; livemode: boolean } | null;
  totals: { total_24h: number; processed_24h: number; failed_24h: number; live_24h: number };
  webhook_url: string;
  checks: Check[];
};

const steps = [
  { id: 1, label: "Update secret key" },
  { id: 2, label: "Update webhook endpoint" },
  { id: 3, label: "Update webhook secret" },
  { id: 4, label: "Validate" },
];

export default function AdminStripeKeySwap() {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [ack, setAck] = useState({ keyUpdated: false, endpointUpdated: false, secretUpdated: false });

  const validate = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-readiness");
      if (error) throw error;
      setReport(data as Report);
      (data as Report).ok ? toast.success("All checks passed") : toast.error("Some checks failed");
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { validate(); }, []);

  const copy = (s: string) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  return (
    <AdminShell title="STRIPE LIVE-KEY SWAP">
      <p className="font-body text-muted-foreground max-w-3xl mb-6">
        Walk through swapping Stripe test keys for live keys safely. Validate after each step.
      </p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Steps */}
        <div className="space-y-4">
          {steps.map((s) => (
            <div key={s.id} className={`bg-panel border border-border p-5 ${step === s.id ? "ring-1 ring-primary" : ""}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 grid place-items-center font-mono text-xs ${step > s.id ? "bg-emerald-500 text-black" : step === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{s.id}</span>
                  <h3 className="font-display tracking-widest text-sm">{s.label}</h3>
                </div>
                {step > s.id && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
              </div>

              {s.id === 1 && (
                <div className="text-sm font-body space-y-3">
                  <p>Open the Stripe dashboard, toggle <strong>Live mode</strong>, then copy your live secret key (starts with <code className="font-mono text-xs">sk_live_</code>). Paste it into the secret form below.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" asChild>
                      <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">
                        Stripe API keys <ExternalLink className="w-3 h-3 ml-2" />
                      </a>
                    </Button>
                    <Button onClick={() => { setAck(a => ({ ...a, keyUpdated: true })); setStep(2); }} disabled={step !== 1}>
                      I updated the secret key
                    </Button>
                  </div>
                </div>
              )}

              {s.id === 2 && (
                <div className="text-sm font-body space-y-3">
                  <p>In Stripe → Developers → Webhooks, add or edit the endpoint to point to your live webhook URL below. Subscribe to <code className="font-mono text-xs">checkout.session.completed</code>, <code className="font-mono text-xs">payment_intent.*</code>, and <code className="font-mono text-xs">charge.refunded</code>.</p>
                  <div className="bg-surface border border-border p-3 flex items-center justify-between gap-2">
                    <code className="font-mono text-xs break-all">{report?.webhook_url ?? "—"}</code>
                    <Button variant="ghost" size="sm" onClick={() => report?.webhook_url && copy(report.webhook_url)}><Copy className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" asChild>
                      <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer">
                        Stripe webhooks <ExternalLink className="w-3 h-3 ml-2" />
                      </a>
                    </Button>
                    <Button onClick={() => { setAck(a => ({ ...a, endpointUpdated: true })); setStep(3); }} disabled={step !== 2}>
                      Endpoint configured
                    </Button>
                  </div>
                </div>
              )}

              {s.id === 3 && (
                <div className="text-sm font-body space-y-3">
                  <p>From the webhook endpoint page, reveal the <strong>signing secret</strong> (starts with <code className="font-mono text-xs">whsec_</code>) and update <code className="font-mono text-xs">STRIPE_WEBHOOK_SECRET</code> in your secret store.</p>
                  <Button onClick={() => { setAck(a => ({ ...a, secretUpdated: true })); setStep(4); validate(); }} disabled={step !== 3}>
                    Secret updated — validate
                  </Button>
                </div>
              )}

              {s.id === 4 && (
                <div className="text-sm font-body space-y-3">
                  <div className="flex items-center gap-2">
                    <Button onClick={validate} disabled={loading}>
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Re-run validation
                    </Button>
                    {report && (
                      <Badge variant={report.ok ? "default" : "destructive"}>
                        {report.checks.filter(c => c.ok).length}/{report.checks.length} passed
                      </Badge>
                    )}
                  </div>
                  {report && (
                    <ul className="space-y-1.5 mt-2">
                      {report.checks.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 text-destructive mt-0.5" />}
                          <span><strong>{c.name}</strong>{c.detail && <span className="text-muted-foreground"> — {c.detail}</span>}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Status panel */}
        <aside className="bg-panel border border-border p-5 h-fit sticky top-24 space-y-3 text-sm font-body">
          <h3 className="font-display tracking-widest text-xs mb-2">CURRENT STATE</h3>
          <Row label="Mode" value={
            report ? <Badge variant={report.mode === "live" ? "default" : "secondary"}>{report.mode.toUpperCase()}</Badge> : "—"
          } />
          <Row label="Account" value={report?.account?.id ?? "—"} mono />
          <Row label="Charges" value={report?.account?.charges_enabled ? "Enabled" : "Disabled"} />
          <Row label="Payouts" value={report?.account?.payouts_enabled ? "Enabled" : "Disabled"} />
          <Row label="Webhook secret" value={report?.webhook_secret_present ? "Set" : "Missing"} />
          <Row label="Webhooks 24h" value={`${report?.totals?.processed_24h ?? 0} ok / ${report?.totals?.failed_24h ?? 0} failed`} />
          <Row label="Last processed" value={report?.last_processed ? `${report.last_processed.type} · ${new Date(report.last_processed.processed_at).toLocaleString()}` : "—"} />
          {report && report.mode === "live" && report.totals.live_24h === 0 && (
            <div className="flex gap-2 text-xs text-amber-400 mt-2"><AlertTriangle className="w-3 h-3 mt-0.5" /> No live events received in last 24h. Send a test event from Stripe to confirm delivery.</div>
          )}
        </aside>
      </div>
    </AdminShell>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
      <span className={mono ? "font-mono text-xs break-all text-right" : "text-xs text-right"}>{value}</span>
    </div>
  );
}